import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Crown,
  Gamepad2,
  Globe,
  Loader2,
  Lock,
  LogOut,
  Settings,
  Shield,
  Trophy,
  UserPlus,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Avatar, Badge, Button } from '../../../../components/ui';
import { DiscordIcon, XIcon } from '../../../../components/icons/BrandIcons';
import { ClubHubView } from '../../../../components/club/ClubHubView';
import { trpc } from '../../../../lib/trpc';

export const Route = createFileRoute('/app/clubs/$slug/')({
  component: ClubDetailPage,
});

// ---------------------------------------------------------------------------
// Privacy config
// ---------------------------------------------------------------------------

const privacyConfig = {
  public: { icon: Globe, label: 'Public', color: 'green' as const },
  private: { icon: Lock, label: 'Private', color: 'yellow' as const },
  invite_only: { icon: Shield, label: 'Invite Only', color: 'purple' as const },
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function ClubDetailPage() {
  const { slug } = Route.useParams();

  const { data: club, isLoading, error } = trpc.club.get.useQuery({ slug });
  const { data: myClubs } = trpc.club.getMyClubs.useQuery();
  const membership = myClubs?.find((c) => c.slug === slug);
  const isMember = !!membership;

  const utils = trpc.useUtils();

  const joinMutation = trpc.clubMember.join.useMutation({
    onSuccess: () => {
      toast.success('Joined club!');
      utils.club.getMyClubs.invalidate();
      utils.club.get.invalidate({ slug });
    },
    onError: (err) => {
      if (err.data?.code === 'CONFLICT') {
        toast.error('You are already a member of this club');
      } else if (err.data?.code === 'FORBIDDEN') {
        toast.error('This club is not accepting new members');
      } else {
        toast.error('Failed to join club');
      }
    },
  });

  const leaveMutation = trpc.clubMember.leave.useMutation({
    onSuccess: () => {
      toast.success('Left club');
      utils.club.getMyClubs.invalidate();
      utils.club.get.invalidate({ slug });
    },
    onError: (err) => {
      if (err.data?.code === 'FORBIDDEN') {
        toast.error(err.message);
      } else {
        toast.error('Failed to leave club');
      }
    },
  });

  if (isLoading) return <ClubDetailSkeleton />;

  if (error || !club) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <div className="relative mb-6">
          <div className="absolute inset-0 rounded-full bg-red-500/15 blur-2xl scale-150" />
          <div className="relative flex items-center justify-center w-20 h-20 rounded-2xl bg-surface-800/80 border border-surface-700/50">
            <Shield className="size-10 text-surface-500" />
          </div>
        </div>
        <h2 className="text-xl font-display font-bold text-white mb-2">Club not found</h2>
        <p className="text-sm text-surface-400 mb-6">This club doesn't exist or has been removed.</p>
        <Link to="/app/clubs">
          <Button variant="outline">Browse Clubs</Button>
        </Link>
      </div>
    );
  }

  const privacy = privacyConfig[club.privacy];
  const PrivacyIcon = privacy.icon;
  const isPublic = club.privacy === 'public';
  const isPrivate = club.privacy === 'private';
  const isInviteOnly = club.privacy === 'invite_only';

  // Private clubs: non-members see only banner + name + locked message
  const showLockedView = isPrivate && !isMember;

  return (
    <div className="relative max-w-4xl mx-auto space-y-10">
      {/* ── Banner ──────────────────────────────────────────────────── */}
      <div>
        <div className="relative h-48 rounded-2xl overflow-hidden">
          {club.bannerUrl ? (
            <img src={club.bannerUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-brand-900/60 via-surface-800 to-surface-950" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-surface-900 via-surface-900/40 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(38,217,181,0.12)_0%,_transparent_60%)]" />
          <div className="absolute inset-0 noise-overlay" />

          <div className="absolute top-3 right-3">
            <Badge variant="subtle" color={privacy.color} size="sm" className="backdrop-blur-sm bg-surface-900/60">
              <PrivacyIcon className="size-3 mr-1" />
              {privacy.label}
            </Badge>
          </div>
        </div>

        {/* ── Header info ─────────────────────────────────────────── */}
        <div className="relative px-1 -mt-10">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            {/* Avatar */}
            <div className="relative shrink-0">
              {club.avatarUrl ? (
                <img
                  src={club.avatarUrl}
                  alt={club.name}
                  className="w-20 h-20 rounded-2xl bg-surface-700 object-cover ring-4 ring-surface-900"
                />
              ) : (
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-surface-700 to-surface-800 flex items-center justify-center text-white font-display font-bold text-3xl ring-4 ring-surface-900">
                  {club.name.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* Name + meta */}
            <div className="flex-1 min-w-0 pb-1">
              <h1 className="text-2xl sm:text-3xl font-display font-bold text-white truncate">
                {club.name}
              </h1>
              {!showLockedView && club.description && (
                <p className="text-sm text-surface-400 mt-1 line-clamp-2">{club.description}</p>
              )}
            </div>

            {/* Action buttons */}
            <div className="shrink-0 pb-1 flex items-center gap-2">
              {(membership?.role === 'owner' || membership?.role === 'admin') && (
                <Link to="/app/clubs/$slug/settings" params={{ slug }}>
                  <Button variant="outline" size="md" className="!px-3">
                    <Settings className="size-4" />
                  </Button>
                </Link>
              )}
              {isMember ? (
                <Button
                  variant="outline"
                  size="md"
                  leftIcon={
                    leaveMutation.isPending
                      ? <Loader2 className="size-4 animate-spin" />
                      : <LogOut className="size-4" />
                  }
                  disabled={leaveMutation.isPending}
                  onClick={() => leaveMutation.mutate({ clubId: club.id })}
                >
                  {leaveMutation.isPending ? 'Leaving...' : 'Leave Club'}
                </Button>
              ) : isPublic ? (
                <Button
                  variant="primary"
                  size="md"
                  leftIcon={
                    joinMutation.isPending
                      ? <Loader2 className="size-4 animate-spin" />
                      : <UserPlus className="size-4" />
                  }
                  disabled={joinMutation.isPending}
                  onClick={() => joinMutation.mutate({ clubId: club.id })}
                >
                  {joinMutation.isPending ? 'Joining...' : 'Join Club'}
                </Button>
              ) : (
                <Button variant="outline" size="md" disabled leftIcon={<Lock className="size-4" />}>
                  {isInviteOnly ? 'Invite Only' : 'Private'}
                </Button>
              )}
            </div>
          </div>

          {/* Stats + social row */}
          {!showLockedView && (
            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-surface-400">
              <div className="flex items-center gap-1.5">
                <Users className="size-4 text-surface-500" />
                <span className="font-semibold tabular-nums text-surface-200">{club.memberCount.toLocaleString()}</span>
                <span>members</span>
              </div>
              {club.onlineMemberCount > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <span className="font-semibold tabular-nums text-green-400">{club.onlineMemberCount}</span>
                  <span>online</span>
                </div>
              )}

              {(club.discordUrl || club.twitterUrl || club.websiteUrl) && (
                <div className="h-4 w-px bg-surface-700" />
              )}

              {club.discordUrl && (
                <a href={club.discordUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-surface-400 hover:text-[#5865F2] transition-colors">
                  <DiscordIcon className="size-4" />
                  Discord
                </a>
              )}
              {club.twitterUrl && (
                <a href={club.twitterUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-surface-400 hover:text-white transition-colors">
                  <XIcon className="size-3.5" />
                  Twitter
                </a>
              )}
              {club.websiteUrl && (
                <a href={club.websiteUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-surface-400 hover:text-brand-400 transition-colors">
                  <Globe className="size-3.5" />
                  Website
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Private club locked view ───────────────────────────────── */}
      {showLockedView ? (
        <PrivateClubLocked />
      ) : isMember ? (
        <ClubHubView clubId={club.id} slug={slug} />
      ) : (
        <>
          {/* ── Games ──────────────────────────────────────────────────── */}
          <GamesSection clubId={club.id} />

          {/* ── Members Preview ────────────────────────────────────────── */}
          <MembersPreview clubId={club.id} memberCount={club.memberCount} />

          {/* ── Leaderboard ────────────────────────────────────────────── */}
          <PublicLeaderboardPlaceholder />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Private Club Locked View
// ---------------------------------------------------------------------------

function PrivateClubLocked() {
  return (
    <div className="flex flex-col items-center justify-center py-20 rounded-xl bg-surface-800/30 border border-surface-700/30 text-center">
      <div className="relative mb-5">
        <div className="absolute inset-0 rounded-full bg-yellow-500/10 blur-2xl scale-150" />
        <div className="relative flex items-center justify-center w-16 h-16 rounded-2xl bg-surface-800/80 border border-surface-700/50">
          <Lock className="size-8 text-yellow-400" />
        </div>
      </div>
      <h3 className="text-lg font-display font-bold text-white mb-2">This club is private</h3>
      <p className="text-sm text-surface-400 max-w-xs">
        You need an invite link from a club member to join this club.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Games Section (non-member public view)
// ---------------------------------------------------------------------------

function GamesSection({ clubId }: { clubId: string }) {
  const { data: games, isLoading } = trpc.clubGame.list.useQuery({ clubId });

  return (
    <section>
      <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500 mb-3">Games</h3>
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 rounded-xl bg-surface-800/50 animate-pulse" />
          ))}
        </div>
      ) : games && games.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {games.map((game) => (
            <div
              key={game.id}
              className="flex items-center gap-3 p-4 rounded-xl bg-surface-800/50 border border-surface-700/50"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-brand-500/10 border border-brand-500/20">
                <Gamepad2 className="size-5 text-brand-400" />
              </div>
              <div className="min-w-0">
                <p className="font-display font-bold text-white truncate">{game.name}</p>
                {game.teamSize && (
                  <p className="text-xs text-surface-400">{game.teamSize}v{game.teamSize}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-surface-500">No games configured yet.</p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Members Preview (non-member public view)
// ---------------------------------------------------------------------------

function MembersPreview({ clubId, memberCount }: { clubId: string; memberCount: number }) {
  const { data, isLoading } = trpc.clubMember.list.useQuery({ clubId, limit: 12 });
  const members = data?.items ?? [];

  return (
    <section>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500">
          Members
          <span className="ml-2 text-surface-600 font-normal normal-case tracking-normal">
            {memberCount.toLocaleString()}
          </span>
        </h3>
      </div>

      {isLoading ? (
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="w-9 h-9 rounded-lg bg-surface-800/50 animate-pulse" />
          ))}
        </div>
      ) : members.length === 0 ? (
        <p className="text-sm text-surface-500">No members yet.</p>
      ) : (
        <div className="space-y-3">
          {/* Avatar strip */}
          <div className="flex flex-wrap gap-1.5">
            {members.map((member) => {
              const isOnline = member.status === 'online';
              return (
                <div key={member.id} className="relative group" title={member.username}>
                  <Avatar
                    size="sm"
                    variant="rounded"
                    src={member.avatarUrl}
                    initials={member.username.charAt(0)}
                    alt={member.username}
                  />
                  {isOnline && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-400 border-2 border-surface-900" />
                  )}
                  {member.role === 'owner' && (
                    <span className="absolute -top-1 -right-1">
                      <Crown className="size-3 text-yellow-400" />
                    </span>
                  )}
                </div>
              );
            })}
            {memberCount > members.length && (
              <div className="w-9 h-9 rounded-lg bg-surface-800/80 border border-surface-700/50 flex items-center justify-center text-xs font-semibold text-surface-400">
                +{memberCount - members.length}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Leaderboard Section (non-member placeholder)
// ---------------------------------------------------------------------------

function PublicLeaderboardPlaceholder() {
  return (
    <section>
      <h3 className="text-sm font-bold uppercase tracking-widest text-surface-500 mb-3">Leaderboard</h3>
      <div className="flex flex-col items-center justify-center py-16 rounded-xl bg-surface-800/30 border border-surface-700/30 text-center">
        <div className="relative mb-4">
          <div className="absolute inset-0 rounded-full bg-brand-500/10 blur-2xl scale-150" />
          <div className="relative flex items-center justify-center w-14 h-14 rounded-2xl bg-surface-800/80 border border-surface-700/50">
            <Trophy className="size-7 text-surface-500" />
          </div>
        </div>
        <h4 className="text-base font-display font-bold text-white mb-1">Coming soon</h4>
        <p className="text-sm text-surface-400 max-w-xs">
          Play matches to climb the ranks. Leaderboards will appear once games are active.
        </p>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Skeletons
// ---------------------------------------------------------------------------

function ClubDetailSkeleton() {
  return (
    <div className="relative max-w-4xl mx-auto space-y-10">
      {/* Banner + header */}
      <div>
        <div className="h-48 rounded-2xl bg-surface-800/50 animate-pulse" />
        <div className="relative px-1 -mt-10">
          <div className="flex items-end gap-4">
            <div className="w-20 h-20 rounded-2xl bg-surface-700 ring-4 ring-surface-900 animate-pulse" />
            <div className="flex-1 pb-1 space-y-2">
              <div className="h-7 w-48 rounded-lg bg-surface-700/60 animate-pulse" />
              <div className="h-4 w-72 rounded bg-surface-700/40 animate-pulse" />
            </div>
            <div className="h-10 w-28 rounded-full bg-surface-700/40 animate-pulse shrink-0" />
          </div>
          <div className="flex gap-4 mt-4">
            <div className="h-4 w-24 rounded bg-surface-700/30 animate-pulse" />
            <div className="h-4 w-20 rounded bg-surface-700/30 animate-pulse" />
          </div>
        </div>
      </div>

      {/* Games */}
      <div>
        <div className="h-4 w-16 rounded bg-surface-700/40 animate-pulse mb-3" />
        <div className="grid grid-cols-2 gap-3">
          <div className="h-20 rounded-xl bg-surface-800/50 animate-pulse" />
          <div className="h-20 rounded-xl bg-surface-800/50 animate-pulse" />
        </div>
      </div>

      {/* Members */}
      <div>
        <div className="h-4 w-24 rounded bg-surface-700/40 animate-pulse mb-3" />
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="w-9 h-9 rounded-lg bg-surface-800/50 animate-pulse" />
          ))}
        </div>
      </div>

      {/* Leaderboard */}
      <div>
        <div className="h-4 w-28 rounded bg-surface-700/40 animate-pulse mb-3" />
        <div className="h-48 rounded-xl bg-surface-800/30 animate-pulse" />
      </div>
    </div>
  );
}
